import { useState } from "react";
import { Icon } from "@mui/material";

import MDBox from "components/MDBox";
import MDButton from "components/MDButton";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";

import BasicTable from "ui-components/tables/BasicTable";
import { ListLayout } from "ui-components/Layout";
import { EditButton, DelButton } from "ui-components/IconButton";
import { Success, Error, MessageDialog } from "ui-components/Alert";
import { getKPIProductivityRangeList } from "services/data.kpi.service";
import { deactivateKPIProductivityRange } from "services/data.kpi.service";
import { ActiveButton } from "ui-components/IconButton";
import { activateKPIProductivityRange } from "services/data.kpi.service";
import BasicTabs from "ui-components/BasicTabs";
import { TableCard } from "ui-components/Layout";
import { getKPIProductivityRangeStatusCount } from "services/data.kpi.service";
import { useTeamValue } from "providers/TeamContext";
import { useCustomerValue } from "providers/CustomerContext";

function KPIProductivityRangeList() {
  const navigate = useNavigate();
  const { permissions, allowAll } = useTeamValue();
  const { customerCode } = useCustomerValue();
  const [alert, setAlert] = useState(null);
  const [dialog, setDialog] = useState(null);
  const [data, setData] = useState([]);
  const [status, setStatus] = useState("Active");
  const active = status === "Active" ? true : false;
  const [tabs, setTabs] = useState([
    { label: "Active", count: 0 },
    { label: "InActive", count: 0 },
  ]);
  const queryClient = useQueryClient();
  const onSuccess = (data) => {
    queryClient.invalidateQueries(["kpi-productivity-range-list"]);
    setAlert(<Success data={data} onChange={setAlert} />);
  };
  const onError = (data) => setAlert(<Error data={data} onChange={setAlert} />);

  const addPermission =
    allowAll ||
    (Boolean(permissions["KPI Productivity"]) &&
      permissions["KPI Productivity"].findIndex((b) => b === "new") !== -1);
  const editPermission =
    allowAll ||
    (Boolean(permissions["KPI Productivity"]) &&
      permissions["KPI Productivity"].findIndex((b) => b === "edit") !== -1);
  const deactivatePermission =
    allowAll ||
    (Boolean(permissions["KPI Productivity"]) &&
      permissions["KPI Productivity"].findIndex((b) => b === "deactivate") !== -1);

  useQuery({
    queryKey: ["kpi-productivity-range-list", { customerCode, active }],
    queryFn: () => getKPIProductivityRangeList({ active }),
    onSuccess: (data) => {
      setData(data.data.data);
    },
    onError,
  });

  useQuery({
    queryKey: ["kpi-productivity-range-list", "status", { customerCode }],
    queryFn: () => getKPIProductivityRangeStatusCount(),
    onSuccess: (data) => setTabs(data.data.data),
    onError,
  });

  const delMutation = useMutation(deactivateKPIProductivityRange, {
    onSuccess,
    onError,
  });
  const activeMutation = useMutation(activateKPIProductivityRange, {
    onSuccess,
    onError,
  });

  const onNew = () => navigate("/kpi/kpi-productivity-range/add");

  const onEdit = (req) => {
    navigate("/kpi/kpi-productivity-range/edit", {
      state: req,
    });
  };

  const handleClose = () => setDialog(null);

  const handleOk = (req) => {
    delMutation.mutate(req);
    setDialog(null);
  };

  const onDelete = (req) => {
    setDialog(
      <MessageDialog
        title={`Deactivate ${req.name}`}
        onClose={handleClose}
        onOk={() => {
          handleOk(req);
        }}
      />
    );
  };
  const onActive = (req) => {
    setDialog(
      <MessageDialog
        title={`Activate ${req.name}`}
        onClose={() => setDialog(null)}
        onOk={() => {
          activeMutation.mutate(req);
          setDialog(null);
        }}
      />
    );
  };

  const columns = [
    { header: "Name", accessorKey: "name" },
    {
      header: "Range", 
      accessorKey: "range",
      cell: (props) => {
        const raw = props.getValue() || '';
        const parts = raw.split(';');

        const formatted = [];
        const colors = ['#f44336', '#ff9800', '#ffeb3b', '#03a9f4', '#4caf50'];

        for (let i = 0; i < parts.length; i++) {
          const sub = parts[i].split(',');
          let text = (i !== 0 && i !== parts.length - 1) ?
             (sub.every(x => x === '0') ? "" : sub.join(' ~ ') + ',') :
             sub;

          if (i === 0) text = '< ' + text + ',';
          else if (i === parts.length - 1) text = ' >' + text;

          if (Boolean(text))
            formatted.push(
              <span key={i} style={{ backgroundColor: colors[i], fontWeight:"bold" }}>
                &nbsp;{text}&nbsp;
              </span>
            );
        }

        return <div style={{ display: 'flex', flexWrap: 'wrap' }}>{formatted}</div>;
      },
    },
    {
      header: "Action",
      accessorKey: "action",
      enableColumnFilter: false,
      cell: ({ row }) => {
        const { name } = row.original;
        if (active) {
          return (
            <>
              <EditButton
                title="edit kpi productivity range"
                disabled={!editPermission}
                onClick={() => {
                  onEdit({ name });
                }}
              />
              <DelButton
                title="deactivate kpi productivity range"
                disabled={!deactivatePermission}
                onClick={() => {
                  onDelete({ name });
                }}
              />
            </>
          );
        } else {
          return (
            <ActiveButton
              title="activate kpi productivity range"
              disabled={!editPermission}
              onClick={() => onActive({ name })}
            />
          );
        }
      },
    },
  ];
  return (
    <ListLayout>
      {dialog}
      <MDBox my={3}>
        {alert}
        <BasicTabs tabs={tabs} status={status} setStatus={setStatus} />
      </MDBox>
      <TableCard>
        <MDBox display="flex" justifyContent="space-between" margin="10px">
          <MDButton variant="gradient" color="info" onClick={onNew} disabled={!addPermission}>
            <Icon>add</Icon>&nbsp;KPI&nbsp;Productivity&nbsp;Range
          </MDButton>
        </MDBox>
        <BasicTable columns={columns} data={data} />
      </TableCard>
    </ListLayout>
  );
}

export default KPIProductivityRangeList;
